import { Box, Button, Menu, MenuItem } from '@mui/material';
import React, { useContext, useState } from 'react';
import { FormContext, FormContextType } from '../../../../form/FormContext';
import { failedToastMessage } from '../../../../utils/commonUtils';
import { DEFAULT_SHARE_ACTION } from '../../../constants';
import type { ShareAccessLevel, ShareSettingsFormState, ShareSettingsViewModel } from '../../../types';
import { useInternalShareSettings } from '../../hooks/useInternalShareSettings';
import ShareAccessLevelLabel from '../dataDisplay/ShareAccessLevelLabel';

const ACCESS_LEVELS: ShareAccessLevel[] = ['open', 'restricted'];
const SHARE_SECURITY_TEST_ID = 'shareSettingsSecurity';

export default function ShareAccessLevelFormField() {
    const {
        shareSettings,
        updateShareSettingsState,
        shareAccessEntryForEntityRecordOwner,
        shareAccessEntryForLoggedInUser,
        backedUpSharedWith,
        setBackedUpSharedWith,
        backedUpSharedWithDepartments,
        setBackedUpSharedWithDepartments
    } = useInternalShareSettings();
    const selectedAccessLevel = shareSettings.accessLevel;

    const { handleChange } = useContext<FormContextType<ShareSettingsFormState>>(FormContext);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    /**
     * When the user selects an access level, we need to update the share settings state accordingly.
     *
     * - If the user selects "open", we clear the sharedWith list and set notify to false.
     * - If the user selects "restricted" and there are no entries in sharedWith,
     *   - If the note record owner is known, we add the owner first, then the logged-in user if they are not the owner.
     *   - If the record owner is not known (new note), then we add the logged-in user.
     *   - If neither the record owner nor the logged-in user is available, we show a failure message.
     *
     * Finally, we close the menu after updating the state.
     */
    const handleSelect = (accessLevel: ShareAccessLevel) => {
        const updatedShareSettings: Partial<ShareSettingsViewModel> = { accessLevel };

        if (accessLevel === 'open') {
            setBackedUpSharedWith(shareSettings.sharedWith);
            setBackedUpSharedWithDepartments(shareSettings.sharedWithDepartments);

            updatedShareSettings.sharedWith = [];
            updatedShareSettings.sharedWithDepartments = [];
            updatedShareSettings.notify = true;

            handleChange('defaultAction', DEFAULT_SHARE_ACTION);
        }

        if (
            accessLevel === 'restricted' &&
            shareSettings.sharedWith.length === 0 &&
            shareSettings.sharedWithDepartments.length === 0
        ) {
            if (backedUpSharedWith.length > 0 || backedUpSharedWithDepartments.length > 0) {
                updatedShareSettings.sharedWith = [...backedUpSharedWith];
                updatedShareSettings.sharedWithDepartments = [...backedUpSharedWithDepartments];
                setBackedUpSharedWith([]);
                setBackedUpSharedWithDepartments([]);
            } else if (shareAccessEntryForEntityRecordOwner) {
                updatedShareSettings.sharedWith = [shareAccessEntryForEntityRecordOwner];

                if (
                    shareAccessEntryForLoggedInUser &&
                    shareAccessEntryForLoggedInUser.actor.id !== shareAccessEntryForEntityRecordOwner.actor.id
                ) {
                    updatedShareSettings.sharedWith.push(shareAccessEntryForLoggedInUser);
                }
            } else if (shareAccessEntryForLoggedInUser) {
                updatedShareSettings.sharedWith = [{ ...shareAccessEntryForLoggedInUser, isOwner: true }];
            } else {
                failedToastMessage('common.share.settings.update.failure');
                handleMenuClose();
                return;
            }
        }

        updateShareSettingsState(updatedShareSettings);

        handleMenuClose();
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Button
                color="inherit"
                onClick={handleMenuOpen}
                data-testid={SHARE_SECURITY_TEST_ID}
                data-share-security-state={selectedAccessLevel}
                fullWidth
                sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    px: 0,
                    py: 0,
                    backgroundColor: 'transparent'
                }}
            >
                <ShareAccessLevelLabel accessLevel={selectedAccessLevel} isButtonLabel />
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                {ACCESS_LEVELS.map((accessLevel) => (
                    <MenuItem
                        key={accessLevel}
                        data-testid={`${SHARE_SECURITY_TEST_ID}Option-${accessLevel}`}
                        selected={accessLevel === selectedAccessLevel}
                        onClick={() => handleSelect(accessLevel)}
                    >
                        <ShareAccessLevelLabel accessLevel={accessLevel} />
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}
